import unique from 'array-unique';
import Prism from 'prismjs';
import defaultComponents from 'prismjs/components';
import getLoader from 'prismjs/dependencies';

import { customPrismSyntaxes } from './custom-prism';
import { defaultPrismSyntaxes } from './prism';

const TITLES = {
    tsx: 'TypeScript JSX (TSX)',
    jsx: 'JavaScript JSX',
};

const prismSyntaxes = {
    ...defaultPrismSyntaxes,
    ...customPrismSyntaxes,
};

// Extend the listing of syntaxes with the custom components we are loading.
const prismComponents: Record<
    string,
    Record<
        string,
        { title?: string; aliasTitles?: Record<string, string>; alias?: string | string[] }
    >
> = {
    ...defaultComponents,
    languages: {
        ...defaultComponents.languages,
        prisma: {
            title: 'Prisma',
        },
    },
};

export interface CodeBlockSyntaxInfo {
    key: string;
    title: string;
    prismComponent?: string;
}

let initialized = false;
const allSyntaxes: CodeBlockSyntaxInfo[] = [];
const allSyntaxesByKey: Map<string, CodeBlockSyntaxInfo> = new Map();
const aliases: Map<string, string> = new Map();

// Remove TypoScript, anyway that's not supported by Shiki
// and it creates confusion with TypeScript.
const DISALLOWED_SYNTAXES = new Set(['typoscript']);

function initializedAllPrismSyntaxes() {
    if (initialized) {
        return;
    }
    initialized = true;

    const indexSyntax = (syntax: CodeBlockSyntaxInfo) => {
        allSyntaxes.push(syntax);
        allSyntaxesByKey.set(syntax.key, syntax);
    };

    indexSyntax({
        key: 'plain',
        title: 'Plain text',
    });

    Object.entries(prismComponents.languages).forEach(([syntaxKey, syntax]) => {
        if (DISALLOWED_SYNTAXES.has(syntaxKey)) {
            return;
        }

        if (TITLES[syntaxKey]) {
            indexSyntax({
                key: syntaxKey,
                title: TITLES[syntaxKey],
                prismComponent: syntaxKey,
            });
            return;
        }

        if (syntax.title) {
            indexSyntax({
                key: syntaxKey,
                title: syntax.title,
                prismComponent: syntaxKey,
            });
        }

        if (syntax.aliasTitles) {
            Object.entries(syntax.aliasTitles).forEach(([aliasKey, aliasTitle]) => {
                indexSyntax({
                    key: aliasKey,
                    title: aliasTitle,
                    prismComponent: syntaxKey,
                });
            });
        }

        if (syntax.alias) {
            if (Array.isArray(syntax.alias)) {
                syntax.alias.forEach((aliasKey) => {
                    aliases.set(aliasKey, syntaxKey);
                });
            } else {
                aliases.set(syntax.alias, syntaxKey);
            }
        }
    });
}

/**
 * Resolve a syntax key to a Prism syntax. It defaults to plain text if not found.
 */
export function resolveCodeBlockSyntax(syntax: string): CodeBlockSyntaxInfo {
    initializedAllPrismSyntaxes();
    const syntaxInfo = allSyntaxesByKey.get(syntax);
    if (syntaxInfo) {
        return syntaxInfo;
    }

    const syntaxAlias = aliases.get(syntax);
    if (syntaxAlias) {
        const syntaxInfo = allSyntaxesByKey.get(syntaxAlias);
        if (syntaxInfo) {
            return syntaxInfo;
        }
    }

    const plainSyntaxInfo = allSyntaxesByKey.get('plain');
    if (!plainSyntaxInfo) {
        throw new Error('Plain text syntax is missing');
    }
    return plainSyntaxInfo;
}

/**
 * Get global cache key for Prism loaded components.
 * This key should be used to know when the set of components loaded from prism changed.
 */
export function getLoadedComponentsCacheKey(): string {
    return loadedComponentsCacheKey;
}

/**
 * Update the cache key for loaded components rom Prism.
 */
function pushLoadedComponents(ids: string[]) {
    if (ids.length === 0) {
        return;
    }

    loadedComponents.push(...ids);
    loadedComponents = unique(loadedComponents).sort();
    loadedComponentsCacheKey = loadedComponents.join('-');
}

/**
 * Reset the list of loaded components.
 * This function must be called between servr-side renderings.
 */
export function resetCodeBlockSyntaxesState() {
    loadedComponents = [];
    loadedComponentsCacheKey = '';

    pushLoadedComponents(initialLoadedComponents);
}

/**
 * List of prism component already loaded.
 *
 * Prism already includes some
 * we don't need to validate the ids because `getLoader` will ignore invalid ones
 */
let loadedComponents: string[] = [];
let loadedComponentsCacheKey = '';

const initialLoadedComponents = Object.keys(Prism.languages);
pushLoadedComponents(initialLoadedComponents);

/**
 * Load syntaxes from prism.
 * If syntaxes are already loaded this function synchronously returns.
 * Otherwise it will load the syntaxes asynchronously and return a promise.
 */
function loadSyntaxes(componentsToLoad: string[]): Promise<void> | null {
    const loader = getLoader(prismComponents, componentsToLoad, loadedComponents);

    const result = loader.load(
        (id) => {
            const loader = prismSyntaxes[id];
            if (!loadedComponents.includes(id)) {
                return loader().then(() => {
                    pushLoadedComponents([id]);
                });
            }
        },
        {
            series: (before, after) => {
                if (before instanceof Promise) {
                    return before.then(() => after());
                }
                return after();
            },
            parallel: (pendings) => {
                const promises = pendings.filter((p) => p instanceof Promise);
                if (promises.length === 0) {
                    return;
                }

                return Promise.all(promises);
            },
        }
    );

    return result;
}

/**
 * Load prism syntaxes and components they depend on.
 * It suspends rendering.
 */
export function loadCodeBlockSyntaxesSuspend(componentsToLoad: string[]) {
    const result = loadSyntaxes(componentsToLoad);
    if (result instanceof Promise) {
        throw result;
    }
}

/**
 * Load prism syntaxes and components they depend on.
 */
export async function loadCodeBlockSyntaxesAsync(componentsToLoad: string[]): Promise<void> {
    await loadSyntaxes(componentsToLoad);
}

/**
 * List all code block syntaxes
 */
export function listCodeBlockSyntaxes(): CodeBlockSyntaxInfo[] {
    initializedAllPrismSyntaxes();
    return allSyntaxes;
}
